import {
  applyNumberMask,
  formatPtBrDecimal,
  normalizePastedDecimal,
  parsePtBrDecimal,
  removeLastMaskedDigit,
  scaleCanonical,
  type DecimalTypingOptions,
  type NumberMaskOptions,
} from '@record-fill';
import { sanitizeNumberInputValue } from '../lib/sanitizeNumberInputValue';

export interface NumberInputOptions extends DecimalTypingOptions {
  mask?: NumberMaskOptions;
}

type NumberValueChangeHandler = (value: string) => void;

interface FixedScaleMask extends NumberMaskOptions {
  scale: number;
}

function buildNextInputValue(target: HTMLInputElement, insertedValue: string): string {
  const selectionStart = target.selectionStart ?? target.value.length;
  const selectionEnd = target.selectionEnd ?? selectionStart;

  return (
    target.value.slice(0, selectionStart) +
    insertedValue +
    target.value.slice(selectionEnd)
  );
}

function sanitize(text: string, options: NumberInputOptions): string {
  return options.mask ? applyNumberMask(text, options.mask) : sanitizeNumberInputValue(text, options);
}

// Only a fixed number of places fills from the right; a free text keeps its caret where the person
// edits it.
function fixedScaleMask(options: NumberInputOptions): FixedScaleMask | null {
  const mask = options.mask;
  return mask && mask.scale !== null ? { ...mask, scale: mask.scale } : null;
}

function isWholeSelection(target: HTMLInputElement): boolean {
  return target.value.length > 0 && target.selectionStart === 0 && target.selectionEnd === target.value.length;
}

function writeToEnd(target: HTMLInputElement, value: string): void {
  target.value = value;
  target.setSelectionRange(value.length, value.length);
}

export function useNumberInput(getOptions: () => NumberInputOptions = () => ({})) {
  function handleInput(event: Event, handleChange: NumberValueChangeHandler) {
    const target = event.target as HTMLInputElement;
    const options = getOptions();
    const value = sanitize(target.value, options);

    if (fixedScaleMask(options)) writeToEnd(target, value);
    else target.value = value;
    handleChange(value);
  }

  function handleMaskedKeydown(
    event: KeyboardEvent,
    mask: FixedScaleMask,
    handleChange: NumberValueChangeHandler | undefined
  ) {
    const target = event.target as HTMLInputElement;
    const wholeSelection = isWholeSelection(target);
    if (!wholeSelection) target.setSelectionRange(target.value.length, target.value.length);

    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      const next = wholeSelection ? '' : removeLastMaskedDigit(target.value, mask);
      writeToEnd(target, next);
      handleChange?.(next);
      return;
    }

    if (event.key.length !== 1) return;
    const current = wholeSelection ? '' : target.value;
    if (applyNumberMask(current + event.key, mask) === applyNumberMask(current, mask)) {
      event.preventDefault();
    }
  }

  function handleKeydown(event: KeyboardEvent, handleChange?: NumberValueChangeHandler) {
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const options = getOptions();
    const mask = fixedScaleMask(options);
    if (mask) {
      handleMaskedKeydown(event, mask, handleChange);
      return;
    }
    if (event.key.length !== 1) return;

    const target = event.target as HTMLInputElement;
    const nextValue = buildNextInputValue(target, event.key);

    if (sanitize(nextValue, options) !== nextValue) {
      event.preventDefault();
    }
  }

  function handlePaste(event: ClipboardEvent, handleChange: NumberValueChangeHandler) {
    event.preventDefault();

    const target = event.target as HTMLInputElement;
    const options = getOptions();
    const pastedValue = normalizePastedDecimal(event.clipboardData?.getData('text') ?? '');
    const mask = fixedScaleMask(options);

    // A paste the mask would have to cut or unsign is refused whole, like a blocked key; the person
    // never ends up with a number other than the one pasted.
    if (!mask) {
      const next = buildNextInputValue(target, pastedValue);
      const value = sanitize(next, options);
      if (parsePtBrDecimal(value) !== parsePtBrDecimal(sanitizeNumberInputValue(next, options))) return;
      target.value = value;
      handleChange(value);
      return;
    }

    // A pasted "1234" is the number 1.234,00, not four keys typed cents first.
    const canonical = parsePtBrDecimal(pastedValue);
    if (canonical === null) return;
    const scaled = scaleCanonical(canonical, mask.scale);
    const value = applyNumberMask(formatPtBrDecimal(scaled), mask);
    if (parsePtBrDecimal(value) !== scaled) return;
    writeToEnd(target, value);
    handleChange(value);
  }

  function handleBlur(event: FocusEvent, handleChange: NumberValueChangeHandler) {
    const target = event.target as HTMLInputElement;
    const canonical = parsePtBrDecimal(target.value);
    const value = canonical === null ? '' : formatPtBrDecimal(canonical);
    if (value === target.value) return;

    target.value = value;
    handleChange(value);
  }

  return {
    handleInput,
    handleKeydown,
    handlePaste,
    handleBlur,
  };
}
